<template>
  <div
    v-show="showGiveawayDetailsModal"
    class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50 px-4"
  >
    <div
      class="relative bg-gradient-to-r from-[#1E1E1E] via-[#2A2A2A] to-[#3A3A3A] w-full max-w-md md:max-w-lg lg:max-w-xl py-6 px-5 rounded-2xl text-center flex flex-col items-center shadow-xl"
    >
      <!-- Close Button -->
      <button
        @click="$emit('close')"
        class="absolute top-4 right-6 text-gray-300 hover:text-white text-4xl font-bold transition duration-200"
      >
        &times;
      </button>

      <!-- Title at the top -->
      <h2 class="text-yellow-400 text-2xl sm:text-3xl font-bold mt-4 mb-4 text-center drop-shadow">
        {{ giveawayDetails.title }}
      </h2>

      <!-- Status Message Based on Giveaway Status -->
      <p class="text-gray-300 font-semibold text-base mt-2">
        {{ giveawayDetails.status === 'ended' ? 'THIS GIVEAWAY ALREADY ENDED' : '' }}
      </p>

      <!-- Animated Drawing Winners with Dots -->
      <p v-if="giveawayDetails.status === 'stopped'" class="text-gray-300 font-semibold text-base mt-1 mb-5">
        DRAWING WINNERS{{ displayDots }}
      </p>   
        <!-- Loading Spinner -->
      <div v-if="loadingGiveaway" class="flex justify-center items-center min-h-[100px]">
        <span
          class="animate-spin border-2 border-white border-t-transparent rounded-full w-12 h-12"
        ></span>
      </div>

      <!-- Winner List or Fallback Message -->
      <div v-else class="w-full mt-4">
        <div v-if="giveawayData.length > 0">
          <!-- Winners Section -->
          <div class="mb-4 text-left">
            <p class="text-gray-300 font-semibold text-lg flex items-center">
              <i class="fas fa-trophy mr-2 text-yellow-400"></i>
              Winners
            </p>
            <!-- Display Winner List -->
            <div class="mb-5 mt-3">
              <div
                v-for="winner in giveawayData.filter(item => item.isWinner)"
                :key="winner.user?.username"
                class="bg-gradient-to-r from-[#1E1E1E] via-white/30 to-[#3A3A3A] text-white border border-white rounded-lg px-4 py-2 w-full flex justify-between items-center mb-3"
              >
                <span>{{ truncateUsername(winner.user?.username) }} <span class="text-yellow-400"> (Winner)</span></span>
                <span>{{ formatDate(winner.createdAt) }}</span>
              </div>
              <!-- Fallback message if no winners -->
              <div v-if="giveawayData.filter(item => item.isWinner).length === 0" class="text-gray-400 text-center">
                No winners yet.
              </div>
            </div>
          </div>

          <!-- Participants Section -->
          <div class="mb-4 ml-2 text-left">
            <p class="text-gray-300 font-semibold text-lg">Participants</p>
            <!-- Display Participant List -->
            <div>
              <div
                v-for="participant in giveawayData.filter(item => !item.isWinner)"
                :key="participant.user?.username"
                class="bg-black text-white rounded-lg px-4 py-2 flex justify-between items-center mb-3 mt-3"
              >
                <span>{{ truncateUsername(participant.user?.username) }}</span>
                <span>{{ formatDate(participant.createdAt) }}</span>
              </div>
              <!-- Fallback message if no participants -->
              <div v-if="giveawayData.filter(item => !item.isWinner).length === 0" class="text-gray-400 text-center">
                No participants yet.
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-gray-400 italic mb-4">
          No winners or participants yet for this giveaway.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  props: {
    showGiveawayDetailsModal: Boolean,
    giveawayDetails: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      giveawayData: [],
      loadingGiveaway: false,
      dotCount: 0, 
      displayDots: "", 
      dotInterval: null, 
    };
  },
  watch: {
    showGiveawayDetailsModal(val) {
      if (val && this.giveawayDetails && this.giveawayDetails._id) {
        this.loadEntries(this.giveawayDetails);
        this.startDotAnimation(); 
      } else {
        this.stopDotAnimation(); 
      }
    },
  },
  methods: {
    formatDate(dateStr) {
      const date = new Date(dateStr);
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    loadEntries(data) {
      const token = localStorage.getItem("ballyboy-access-token");
      this.loadingGiveaway = true;

      let endpoint = '';

      if (data.status === "ended") {
        endpoint = `/api/giveaways/${data._id}/users?isWinner=true`;
      } else if (data.status === "stopped" || data.status === "started") {
        endpoint = `/api/giveaways/${data._id}/users`;  
      }

      axios
        .get(process.env.VUE_APP_BASE_API + endpoint, {
          headers: {
            "x-api-key": process.env.VUE_APP_X_API_KEY,
            Authorization: `Bearer ${token}`,
          },
        })
        .then((res) => {
          if (res.data) {
            this.giveawayData = res.data.giveawayUsers || [];
          }
        })
        .catch((error) => {
          if (error.response?.status === 401) {
            localStorage.removeItem("vuex");
            localStorage.removeItem("ballyboy-access-token");
            window.location.reload();
          } else {
            console.error("Error fetching giveaway data:", error);
          }
        })
        .finally(() => {
          this.loadingGiveaway = false;
        });
    },
    startDotAnimation() {
      this.dotInterval = setInterval(() => {
        // Update dots every second
        this.dotCount = (this.dotCount + 1) % 4; // Cycle from 0 to 3 dots
        this.displayDots = ".".repeat(this.dotCount); // Update the dot string
      }, 1000);
    },
    stopDotAnimation() {
      clearInterval(this.dotInterval); // Stop the dot animation
      this.dotCount = 0;
      this.displayDots = "";
    },
    truncateUsername(name) {
    if (!name) return "";
    return name.length > 12 ? name.slice(0, 12) + "..." : name;
  },
  },
};
</script>

<style scoped>
.dot-animate {
  animation: dots 1.5s steps(5, end) infinite;
}

@keyframes dots {
  0% {
    content: "."; 
  }
  25% {
    content: "..";
  }
  50% {
    content: "...";
  }
  75% {
    content: "..";
  }
  100% {
    content: ".";
  }
}
</style>
